import type { Calendar, CalendarDate } from '@/@types/gtfs-ressource';
import type { LatLon } from './journeys';

/**
 * An end of the journey as the passenger picked it: their position (departure only),
 * a stop, or a point such as a geocoded address, with the label it was found under.
 */
export type Place =
  { kind: 'position' } | { kind: 'stop'; stopId: string } | ({ kind: 'point'; label?: string } & LatLon);

const POINT_PARAM = /^(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/;

/**
 * `?from=` / `?to=` hold a stop id or « lat,lon », `?fromLabel=` / `?toLabel=` the name
 * of a point. Without `from`, the journey starts from the passenger's position.
 */
export function parsePlaceParam(value: string | undefined, label?: string): Place | undefined {
  if (!value) return undefined;
  const point = POINT_PARAM.exec(value);
  if (point)
    return { kind: 'point', lat: Number(point[1]), lon: Number(point[2]), ...(label ? { label } : {}) };
  return { kind: 'stop', stopId: value };
}

export function placeParams(place: Place | undefined): { value?: string; label?: string } {
  if (place?.kind === 'stop') return { value: place.stopId };
  if (place?.kind === 'point') return { value: `${place.lat},${place.lon}`, label: place.label };
  return {};
}

/** First and last day (YYYYMMDD) the loaded GTFS has any service on. */
export function validityRange(
  calendars: Calendar[],
  calendarDates: CalendarDate[],
): { start: string; end: string } | undefined {
  const days = [
    ...calendars.flatMap(calendar => [calendar.start_date, calendar.end_date]),
    ...calendarDates.filter(date => date.exception_type === 1).map(date => date.date),
  ].sort();
  return days.length > 0 ? { start: days[0], end: days[days.length - 1] } : undefined;
}
